import React from 'react';
import { Link } from 'react-router-dom';
import { Users, Shield, BarChart3, Clock, CheckCircle, ArrowRight, Building2, Lock, FileText, Headphones } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';

const benefits = [
  {
    icon: Shield,
    title: 'Mayor adopción',
    description: 'El anonimato elimina el estigma. Tus colaboradores usarán el servicio sin miedo a ser juzgados.'
  },
  {
    icon: Lock,
    title: 'Reportes agregados',
    description: 'Solo recibes datos estadísticos cuando hay 10+ usuarios (k-anonymity). Nunca identidades individuales.'
  },
  {
    icon: BarChart3,
    title: 'Métricas de bienestar',
    description: 'Dashboards con tendencias de uso, áreas de mayor demanda y ROI del programa.'
  },
  {
    icon: Clock,
    title: 'SLA garantizado',
    description: 'Primera sesión disponible en máximo 72 horas (48h en plan Premium).'
  },
  {
    icon: FileText,
    title: 'Cumplimiento normativo',
    description: 'Ayuda a cumplir con normativas de salud mental laboral y riesgos psicosociales.'
  },
  {
    icon: Headphones,
    title: 'Soporte dedicado',
    description: 'Account manager exclusivo y soporte prioritario para tu organización.'
  }
];

const EmpresasPage = () => {
  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="bg-sage-50 py-20 md:py-28">
        <div className="container-max mx-auto px-4 md:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="bg-sage-100 text-sage-700 hover:bg-sage-100 mb-4">
                <Building2 className="w-3 h-3 mr-1" />
                Para Empresas
              </Badge>
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-semibold text-slate-900 mb-6">
                Programa EAP anónimo para tu equipo
              </h1>
              <p className="text-xl text-slate-600 leading-relaxed mb-8">
                Ofrece apoyo psicológico a tus colaboradores sin comprometer su privacidad. 
                Mayor adopción, menos estigma, mejor clima laboral.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contacto">
                  <Button size="lg" className="btn-sage w-full sm:w-auto" data-testid="empresas-demo-btn">
                    Solicitar demo
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
                <Link to="/contacto">
                  <Button size="lg" variant="outline" className="btn-secondary w-full sm:w-auto">
                    Cotizar programa
                  </Button>
                </Link>
              </div>
            </div>

            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1766603065399-8048428595bd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwxfHxwZXJzb24lMjBzaXR0aW5nJTIwYWxvbmUlMjBwZWFjZWZ1bCUyMGJhY2slMjB2aWV3fGVufDB8fHx8MTc2OTc4OTg2M3ww&ixlib=rb-4.1.0&q=85"
                alt="Persona en ambiente tranquilo"
                className="rounded-3xl shadow-xl w-full object-cover h-[400px]"
              />
              <Card className="absolute -bottom-6 -left-6 glass-card p-4 max-w-xs">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-sage-100 flex items-center justify-center">
                    <Users className="w-6 h-6 text-sage-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">+500 empresas</p>
                    <p className="text-sm text-slate-500">confían en nosotros</p>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="section-padding bg-white">
        <div className="container-max mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-slate-900 mb-4">
              ¿Por qué un EAP anónimo?
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Los programas tradicionales tienen baja adopción por el estigma asociado. 
              Nuestro enfoque anónimo cambia las reglas del juego.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit) => (
              <Card key={benefit.title} className="glass-card card-hover p-8">
                <CardContent className="p-0 space-y-4">
                  <div className="w-12 h-12 rounded-2xl bg-sage-100 flex items-center justify-center">
                    <benefit.icon className="w-6 h-6 text-sage-600" />
                  </div>
                  <h3 className="font-heading text-xl font-semibold text-slate-900">{benefit.title}</h3>
                  <p className="text-slate-600">{benefit.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Models */}
      <section id="planes" className="section-padding bg-slate-50">
        <div className="container-max mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-slate-900 mb-4">
              Modelos comerciales
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Adaptamos el programa a las necesidades y tamaño de tu organización.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* Bolsa de Sesiones */}
            <Card className="border-2 border-sage-200">
              <CardHeader>
                <Badge className="w-fit bg-sage-100 text-sage-700 mb-2">Más flexible</Badge>
                <CardTitle className="font-heading text-2xl">Bolsa de Sesiones</CardTitle>
                <CardDescription>Ideal para empresas medianas</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <p className="text-3xl font-bold text-sage-600">Desde $[valor]</p>
                  <p className="text-slate-500">CLP por sesión</p>
                </div>
                
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-sage-500 shrink-0 mt-0.5" />
                    <span className="text-slate-600">Bolsas desde 50 sesiones</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-sage-500 shrink-0 mt-0.5" />
                    <span className="text-slate-600">Sin vencimiento</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-sage-500 shrink-0 mt-0.5" />
                    <span className="text-slate-600">Reportes mensuales agregados</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-sage-500 shrink-0 mt-0.5" />
                    <span className="text-slate-600">SLA: primera sesión ≤72h</span>
                  </li>
                </ul>

                <Link to="/contacto">
                  <Button className="w-full btn-sage">
                    Cotizar bolsa de sesiones
                  </Button>
                </Link>
              </CardContent>
            </Card>

            {/* PEPM */}
            <Card className="border-2 border-teal-600 relative">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <Badge className="bg-teal-600 text-white">Recomendado</Badge>
              </div>
              <CardHeader>
                <Badge className="w-fit bg-teal-100 text-teal-700 mb-2">Cobertura total</Badge>
                <CardTitle className="font-heading text-2xl">Plan PEPM</CardTitle>
                <CardDescription>Per Employee Per Month</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <p className="text-3xl font-bold text-teal-600">Desde $[valor]</p>
                  <p className="text-slate-500">CLP por colaborador/mes</p>
                </div>
                
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-teal-500 shrink-0 mt-0.5" />
                    <span className="text-slate-600">Sesiones ilimitadas</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-teal-500 shrink-0 mt-0.5" />
                    <span className="text-slate-600">Account manager dedicado</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-teal-500 shrink-0 mt-0.5" />
                    <span className="text-slate-600">Dashboard en tiempo real</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-teal-500 shrink-0 mt-0.5" />
                    <span className="text-slate-600">SLA Premium: ≤48h</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-teal-500 shrink-0 mt-0.5" />
                    <span className="text-slate-600">Webinars de bienestar</span>
                  </li>
                </ul>

                <Link to="/contacto">
                  <Button className="w-full btn-primary">
                    Solicitar demo
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* SLA and Security */}
      <section className="section-padding bg-white">
        <div className="container-max mx-auto">
          <div className="grid md:grid-cols-2 gap-12">
            {/* SLA */}
            <div>
              <h3 className="font-heading text-2xl font-semibold text-slate-900 mb-6">
                Compromisos de servicio (SLA)
              </h3>
              <div className="space-y-4">
                <Card className="p-4">
                  <div className="flex justify-between items-center">
                    <span className="text-slate-600">Primera sesión disponible</span>
                    <Badge className="bg-sage-100 text-sage-700">≤72h (Premium ≤48h)</Badge>
                  </div>
                </Card>
                <Card className="p-4">
                  <div className="flex justify-between items-center">
                    <span className="text-slate-600">Uptime plataforma</span>
                    <Badge className="bg-sage-100 text-sage-700">99.9%</Badge>
                  </div>
                </Card>
                <Card className="p-4">
                  <div className="flex justify-between items-center">
                    <span className="text-slate-600">Respuesta soporte</span>
                    <Badge className="bg-sage-100 text-sage-700">≤24h hábiles</Badge>
                  </div>
                </Card>
                <Card className="p-4">
                  <div className="flex justify-between items-center">
                    <span className="text-slate-600">Implementación</span>
                    <Badge className="bg-sage-100 text-sage-700">3-4 semanas</Badge>
                  </div>
                </Card>
              </div>
            </div>

            {/* Security */}
            <div>
              <h3 className="font-heading text-2xl font-semibold text-slate-900 mb-6">
                Seguridad y privacidad
              </h3>
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-teal-100 flex items-center justify-center shrink-0">
                    <Lock className="w-5 h-5 text-teal-600" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">K-anonymity (n≥10)</p>
                    <p className="text-sm text-slate-600">
                      Solo reportes agregados cuando hay 10+ usuarios. Imposible identificar individuos.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-teal-100 flex items-center justify-center shrink-0">
                    <Shield className="w-5 h-5 text-teal-600" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">Sin grabaciones</p>
                    <p className="text-sm text-slate-600">
                      Las sesiones nunca se graban. Cumplimiento con ley de protección de datos.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-teal-100 flex items-center justify-center shrink-0">
                    <FileText className="w-5 h-5 text-teal-600" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">Cumplimiento normativo</p>
                    <p className="text-sm text-slate-600">
                      Ayuda a cumplir con normativas de riesgos psicosociales y salud laboral.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-sage-600">
        <div className="container-max mx-auto text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-semibold text-white mb-6">
            Cuida el bienestar de tu equipo
          </h2>
          <p className="text-lg text-sage-50 max-w-2xl mx-auto mb-8">
            Agenda una demo personalizada y conoce cómo AnonimaMente puede transformar el bienestar emocional de tu organización.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contacto">
              <Button size="lg" className="bg-white text-sage-600 hover:bg-sage-50 rounded-full px-8 py-6 text-lg">
                Solicitar demo
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/contacto">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 rounded-full px-8 py-6 text-lg">
                Descargar brochure
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default EmpresasPage;
